import { Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useLang } from "@/context/LanguageContext";
import { SITE } from "@/data";

export const Contact = () => {
  const { t } = useLang();
  const waUrl = `https://wa.me/${SITE.whatsappNumber}?text=${encodeURIComponent(t.whatsappMsg)}`;
  return (
    <section id="contact" className="py-20 md:py-28 bg-sand" data-testid="contact-section">
      <div className="px-6 md:px-12 lg:px-20 mx-auto max-w-5xl text-center">
        <p className="font-body text-xs tracking-[0.25em] uppercase text-teal font-bold mb-4">{t.contact.overline}</p>
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-jungle mb-4">{t.contact.title}</h2>
        <p className="font-body text-jungle-light/80 mb-9 max-w-xl mx-auto">{t.contact.subtitle}</p>
        <div className="flex flex-wrap justify-center gap-4">
          <a href={waUrl} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-button" className="inline-flex items-center gap-2 bg-[#25D366] text-white font-body px-8 py-4 hover:opacity-90 transition-opacity">
            <MessageCircle size={18} /> {t.contact.whatsapp}
          </a>
          <a href={`tel:${SITE.phoneDisplay.replace(/\s/g, "")}`} data-testid="contact-call-button" className="inline-flex items-center gap-2 border border-jungle text-jungle font-body px-8 py-4 hover:bg-jungle hover:text-sand transition-colors">
            <Phone size={18} /> {t.contact.call}
          </a>
        </div>
      </div>
    </section>
  );
};

export const Footer = () => {
  const { t } = useLang();
  const links = [
    { label: t.nav.services, href: "#services" },
    { label: t.nav.destinations, href: "#destinations" },
    { label: t.nav.pricing, href: "#pricing" },
    { label: t.nav.book, href: "#book" },
  ];
  const go = (href) => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  return (
    <footer className="bg-jungle text-sand py-16 px-6 md:px-12 lg:px-20" data-testid="main-footer">
      <div className="mx-auto max-w-7xl grid md:grid-cols-3 gap-12">
        <div>
          <Logo variant="light" />
          <p className="font-body text-sm text-sand/70 mt-5 leading-relaxed max-w-xs">{t.footer.tagline}</p>
        </div>
        <div>
          <h4 className="font-heading text-xl font-semibold mb-5">{t.footer.quickLinks}</h4>
          <ul className="space-y-3">
            {links.map((l) => (
              <li key={l.href}>
                <button onClick={() => go(l.href)} className="font-body text-sm text-sand/70 hover:text-teal transition-colors" data-testid={`footer-link-${l.href.replace("#", "")}`}>
                  {l.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="font-heading text-xl font-semibold mb-5">{t.footer.contact}</h4>
          <ul className="space-y-3 font-body text-sm text-sand/70">
            <li className="flex items-center gap-2"><Phone size={15} className="text-teal" /> {SITE.phoneDisplay}</li>
            <li className="flex items-center gap-2"><Mail size={15} className="text-teal" /> {SITE.email}</li>
            <li className="flex items-center gap-2"><MapPin size={15} className="text-teal" /> Cancún · Riviera Maya, MX</li>
          </ul>
        </div>
      </div>
      <div className="mx-auto max-w-7xl border-t border-sand/15 mt-12 pt-8 flex flex-col sm:flex-row justify-between items-center gap-3">
        <a href="https://xctmexico.com" className="font-heading text-lg font-semibold tracking-wide text-teal" data-testid="footer-domain">
          XCTMEXICO.COM
        </a>
        <p className="font-body text-xs text-sand/50">© {new Date().getFullYear()} {SITE.brand}. {t.footer.rights}</p>
        <a href="/admin" className="font-body text-xs text-sand/40 hover:text-teal transition-colors" data-testid="footer-admin-link">Admin</a>
      </div>
    </footer>
  );
};
